import React, { useState } from "react";
import { Link } from "react-router-dom";

import axios from "axios";

import PageTitle from "../../../layouts/PageTitle";
import { useGetNcsDashboardQuery } from "../../../../store/appApi";
import { uiToast } from "./ui";
import {
  usePartnerCode,
  useProjectPartners,
  panelClass,
  btnGhost,
  Badge,
  TRANSITION_TONE,
  fmtDate,
} from "./config";
import { downloadWorkbook } from "../../../../services/netplusCustomerStatus";
import TrendCharts from "./TrendCharts";

const Kpi = ({ label, value, tone }) => (
  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
      {label}
    </div>
    <div className={`mt-1 text-2xl font-semibold tabular-nums ${tone || "text-gray-900"}`}>
      {value ?? "—"}
    </div>
  </div>
);

const Section = ({ title, children, right }) => (
  <section className={panelClass}>
    <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </h2>
      {right}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const NcsDashboard = () => {
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const { codes: allPartnerCodes } = useProjectPartners();
  // Local to this screen only — never written to the shared, cross-page
  // selected partner. Upload/Export/Digest all need one real partner, so
  // they're hidden while this is on; the KPI tiles/trend below combine
  // every partner's numbers into one set of totals.
  const [viewAll, setViewAll] = useState(false);
  const [win, setWin] = useState(7);
  const [busy, setBusy] = useState(false);
  const { data, isLoading, isError, error, refetch } = useGetNcsDashboardQuery({
    partnerCode: viewAll ? "ALL" : partnerCode,
    window: win,
  });

  const d = data || {};
  const b = d.buckets || {};
  const r = d.recharge || {};
  const f = d.followups || {};

  const doExport = async (view) => {
    try {
      setBusy(true);
      await downloadWorkbook(partnerCode, { view });
    } catch (e) {
      uiToast({
        type: "error",
        title: "Export failed",
        message: e?.response?.data?.message || e.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const openDigest = async () => {
    const url = `${process.env.REACT_APP_API_URL || "http://localhost:5001"}/api/netplus/customer-status/digest?partnerCode=${partnerCode}&format=html`;
    // token is on axios defaults, not the browser — fetch then open in a tab
    try {
      setBusy(true);
      const res = await axios.get(url, { responseType: "text" });
      const w = window.open("", "_blank");
      if (w) {
        w.document.write(res.data);
        w.document.close();
      }
    } catch (e) {
      uiToast({
        type: "error",
        title: "Digest failed",
        message: e?.response?.data?.message || e.message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Customer Status" motherMenu="Netplus" />

      {isError && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error?.data?.message ||
            "Could not load the dashboard. Make sure the Customer Status Report project is active and the partner is registered."}
        </div>
      )}

      <div className="space-y-6">
        <section className={`${panelClass} p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className="inline-flex rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-cyan-700">
                Netplus · {viewAll ? "All partners" : d.partner?.partnerCode || partnerCode}
              </span>
              <h1 className="mt-3 text-2xl font-semibold text-gray-900 md:text-3xl">
                Customer Status Report
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                As of{" "}
                <strong>{d.asOfDate ? fmtDate(d.asOfDate) : "no sheet uploaded"}</strong>
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label
                className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-2 py-1"
                style={{ backgroundColor: "#fff" }}
                title="Partner code"
              >
                <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                  Partner
                </span>
                <select
                  className="bg-white text-xs font-semibold text-gray-800 outline-none"
                  style={{ backgroundColor: "#fff", color: "#1f2937" }}
                  value={viewAll ? "ALL" : partnerCode}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "ALL") {
                      setViewAll(true);
                    } else {
                      setViewAll(false);
                      setPartnerCode(v);
                    }
                  }}
                >
                  <option value="ALL" style={{ color: "#1f2937", backgroundColor: "#fff" }}>
                    All partners
                  </option>
                  {(allPartnerCodes.length ? allPartnerCodes : [partnerCode]).map((c) => (
                    <option key={c} value={c} style={{ color: "#1f2937", backgroundColor: "#fff" }}>
                      {c}
                    </option>
                  ))}
                </select>
              </label>
              {!viewAll && (
                <>
                  <Link to="/netplus/customer-status/upload" className={btnGhost}>
                    Upload sheet
                  </Link>
                  <button className={btnGhost} disabled={busy} onClick={() => doExport("full")}>
                    Export Excel
                  </button>
                  <button className={btnGhost} disabled={busy} onClick={openDigest}>
                    Daily digest
                  </button>
                </>
              )}
              <button className={btnGhost} onClick={refetch}>
                Refresh
              </button>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
            <Kpi label="Total" value={b.total} />
            <Kpi label="Active" value={b.active} tone="text-emerald-600" />
            <Kpi label="Expiring ≤7d" value={b.expiring7} tone="text-cyan-600" />
            <Kpi label="Inactive" value={b.inactive} tone="text-amber-600" />
            <Kpi label="Suspended" value={b.suspended} tone="text-rose-600" />
            <Kpi label="Terminated" value={b.terminated} tone="text-gray-400" />
            <Kpi label="Off sheet" value={b.droppedFromSheet} tone="text-rose-500" />
          </div>
        </section>

        {!viewAll && <TrendCharts partnerCode={partnerCode} />}

        {isLoading ? (
          <div className={`${panelClass} p-10 text-center text-gray-400`}>Loading…</div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            <Section
              title={`Recharge — last ${d.windowDays || win} days`}
              right={
                <select
                  className="rounded-lg border border-gray-300 px-2 py-1 text-xs"
                  value={win}
                  onChange={(e) => setWin(Number(e.target.value))}
                >
                  {[7, 14, 30].map((w) => (
                    <option key={w} value={w}>
                      {w} days
                    </option>
                  ))}
                </select>
              }
            >
              <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
                {/* "Reactivated" — a subscription (Inactive/Suspended -> Active)
                    coming back after a recharge. Deliberately NOT called
                    "Recovered" here: that word is also a Modem Recovery case
                    state (a physically recovered modem) and the two are
                    unrelated — see recoveredByChannel below, almost none of
                    which touches Modem Recovery. */}
                <Kpi label="Reactivated" value={r.recoveredTotal} tone="text-emerald-600" />
                <Kpi label="Renewed today" value={r.rechargedToday?.RENEWED} />
                <Kpi label="Lapsed" value={r.lapsed} tone="text-amber-600" />
                <Kpi label="Churned" value={r.churned} tone="text-rose-600" />
              </div>
              <div className="mt-4 text-xs text-gray-500">
                Reactivated by channel —{" "}
                {Object.entries(r.recoveredByChannel || {})
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(" · ")}
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Recharged on latest sheet — Reactivated {r.rechargedToday?.RECOVERED ?? 0},
                Renewed {r.rechargedToday?.RENEWED ?? 0}
              </div>
            </Section>

            <Section title="Overdue age tiers">
              <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
                {Object.entries(d.overdueTiers || {}).map(([k, v]) => (
                  <Kpi key={k} label={`${k} days`} value={v} />
                ))}
              </div>
              <div className="mt-4">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Deactivating next 7 days ({d.deactivations?.next7 ?? 0})
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(d.deactivations?.byDay || []).map((x) => (
                    <span
                      key={x.date}
                      className="rounded-lg border border-gray-200 px-2 py-1 text-[11px] text-gray-600"
                    >
                      {fmtDate(x.date)} · <strong>{x.count}</strong>
                    </span>
                  ))}
                  {!(d.deactivations?.byDay || []).length && (
                    <span className="text-xs text-gray-400">None</span>
                  )}
                </div>
              </div>
            </Section>

            <Section
              title="Follow-up queue"
              right={
                <Link to="/netplus/customer-status/followups" className={btnGhost}>
                  Open board
                </Link>
              }
            >
              <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-4">
                <Kpi label="Open" value={f.open} tone="text-cyan-600" />
                <Kpi label="Unassigned" value={f.unassigned} tone="text-rose-500" />
                <Kpi label="High" value={f.byPriority?.high} />
                <Kpi label="Inactive" value={f.byBucket?.Inactive} />
              </div>
              <div className="mt-3 text-xs text-gray-500">
                By state —{" "}
                {Object.entries(f.byState || {})
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(" · ") || "—"}
              </div>
            </Section>

            <Section
              title="Recent transitions"
              right={
                <Link to="/netplus/customer-status/transitions" className={btnGhost}>
                  View all
                </Link>
              }
            >
              <div className="max-h-72 space-y-1.5 overflow-y-auto">
                {(d.recentTransitions || []).slice(0, 15).map((t) => (
                  <div
                    key={t._id}
                    className="flex items-center justify-between gap-2 border-b border-gray-50 pb-1.5 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <Badge tone={TRANSITION_TONE[t.type]}>{t.type}</Badge>
                      <span className="font-medium text-gray-800">{t.name || t.userId}</span>
                    </div>
                    <span className="text-gray-400">{fmtDate(t.toDate)}</span>
                  </div>
                ))}
                {!(d.recentTransitions || []).length && (
                  <div className="text-xs text-gray-400">
                    No movement yet — needs a second day's sheet.
                  </div>
                )}
              </div>
            </Section>
          </div>
        )}

        {d.uploads?.missingDates?.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Missing sheets between uploads: {d.uploads.missingDates.join(", ")}
          </div>
        )}
      </div>
    </>
  );
};

export default NcsDashboard;
